import {Color} from '@benchling/nucleus-ui';
import {Box, Flex, Paper, Text} from '@mantine/core';
import React from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  FAB,
  FC,
  HETERO_FC,
  IGG,
  SCFAB,
  SCFV,
  IGG_SCFAB,
  FAB_SCFV_HETERO_FC,
  SCFV_FC_SCFV,
} from 'antibodies/format/help-modal/constants';
import Antibody from 'antibodies/visualization/Antibody';

function SyntaxPart({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Paper p="xs" bg="white" withBorder={true} radius="md">
      <Text span={true} fz="md">
        {children}
      </Text>
    </Paper>
  );
}

function VeritasName({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Flex align="center" gap="xs">
      {children}
    </Flex>
  );
}

interface ComponentBoxProps {
  label: string;
  format: AntibodyFormat;
  top: number;
  left: number;
  formatSize?: number;
  backgroundColor?: string;
  textColor?: string;
  isCenter?: boolean;
}

function ComponentBox({
  label,
  format,
  top,
  left,
  formatSize,
  backgroundColor,
  textColor,
  isCenter,
}: ComponentBoxProps): JSX.Element {
  return (
    // Use the style attribute for exact pixel positioning (since mantine converts through rem units)
    <Paper p="sm" radius="md" withBorder={true} pos="absolute" style={{top: `${top}px`, left: `${left}px`}}>
      <Flex direction="column" align="center" gap="xs">
        <Paper bg={backgroundColor || 'gray.1'} radius="md">
          <Flex w={`${formatSize || 70}px`} h={`${formatSize || 70}px`} align="center" justify="center">
            <Antibody format={format} />
          </Flex>
        </Paper>
        <Text span={true} td={isCenter ? 'underline' : 'none'} color={textColor || 'gray.7'} fw="bold">
          {label}
        </Text>
      </Flex>
    </Paper>
  );
}

function ComponentLabel({label, top, left}: {label: string; top: number; left: number}): JSX.Element {
  return (
    // Use the style attribute for exact pixel positioning (since mantine converts through rem units)
    <Box pos="absolute" style={{top: `${top}px`, left: `${left}px`}}>
      <Text span={true} color="gray.5" fs="italic">
        {label}
      </Text>
    </Box>
  );
}

function Arrow({
  top,
  left,
  width,
  height,
  path,
}: {
  top: number;
  left: number;
  width: number;
  height: number;
  path: string;
}): JSX.Element {
  return (
    <Box pos="absolute" style={{top: `${top}px`, left: `${left}px`}}>
      <svg viewBox={`0 0 ${width} ${height}`} width={`${width}px`} height={`${height}px`}>
        <path
          d={path}
          stroke={Color.GRAY_400}
          strokeWidth="1.5"
          fill="none"
          markerEnd="url(#veritas-example-arrowhead)"
        />
      </svg>
    </Box>
  );
}

function Example({
  veritasName,
  height,
  children,
}: {
  veritasName: React.ReactNode;
  height: number;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Flex direction="column" mb="xl" pb="xl" style={{borderBottom: `1px solid ${Color.GRAY_200}`}}>
      <Flex align="center" gap="sm" mb="lg">
        <Text span={true} fw="bold" size="md">
          Example:
        </Text>
        {veritasName}
      </Flex>
      <Paper
        pos="relative"
        h={`${height}px`}
        w="700px"
        bg="gray.0"
        radius="lg"
        style={{overflow: 'hidden', alignSelf: 'center'}}
      >
        <Box pos="absolute" style={{top: height / 2 - 10, left: 20}}>
          {veritasName}
        </Box>
        {children}
      </Paper>
    </Flex>
  );
}

function Example1(): JSX.Element {
  const veritasName = (
    <VeritasName>
      <SyntaxPart>
        <Text span={true} color="gray.5">
          [
        </Text>
        <Text span={true} color="purple.5">
          Fab
        </Text>
        <Text span={true} color="red.5" fw="bold">
          *
        </Text>
        <Text span={true} color="teal.5">
          scFv
        </Text>
        <Text span={true} color="gray.5">
          ]
        </Text>
      </SyntaxPart>
      <Text span={true} color="gray.5">
        -
      </Text>
      <SyntaxPart>
        <Text span={true} td="underline" color="green.8">
          heteroFc
        </Text>
      </SyntaxPart>
    </VeritasName>
  );
  return (
    <Example veritasName={veritasName} height={300}>
      <Arrow top={40} left={20} width={300} height={100} path="M 40 80 l 0 -50 l 180 0" />
      <Arrow top={160} left={100} width={200} height={100} path="M 30 15 l 0 50 l 160 0" />
      <Arrow top={100} left={450} width={100} height={100} path="M 0 50 l 55 0" />
      <ComponentLabel label="N-term" top={120} left={40} />
      <ComponentLabel label="center" top={120} left={110} />
      <ComponentBox label="Fab" format={FAB} top={30} left={250} textColor="purple.5" />
      <ComponentBox label="scFv" format={SCFV} top={30} left={350} textColor="teal.5" />
      <ComponentBox
        label="heteroFc"
        format={HETERO_FC}
        top={160}
        left={300}
        textColor="green.8"
        isCenter={true}
      />
      <ComponentBox
        label="[Fab*scFv]-heteroFc"
        format={FAB_SCFV_HETERO_FC}
        backgroundColor="teal.0"
        formatSize={150}
        top={50}
        left={515}
      />
    </Example>
  );
}

function Example2(): JSX.Element {
  const veritasName = (
    <VeritasName>
      <SyntaxPart>
        <Text span={true} td="underline" color="green.8">
          IgG
        </Text>
      </SyntaxPart>
      <Text span={true} color="gray.5">
        -
      </Text>
      <SyntaxPart>
        <Text span={true} color="gray.5">
          [
        </Text>
        <Text span={true} color="teal.5">
          scFab
        </Text>
        <Text span={true} color="red.5" fw="bold">
          *
        </Text>
        <Text span={true} color="gray.5">
          ]
        </Text>
      </SyntaxPart>
    </VeritasName>
  );
  return (
    <Example veritasName={veritasName} height={300}>
      <Arrow top={40} left={20} width={300} height={100} path="M 20 70 l 0 -30 l 240 0" />
      <Arrow top={160} left={60} width={200} height={100} path="M 30 15 l 0 50 l 150 0" />
      <Arrow top={100} left={410} width={200} height={100} path="M 0 50 l 90 0" />
      <ComponentLabel label="center" top={120} left={20} />
      <ComponentBox label="IgG" format={IGG} top={30} left={300} textColor="green.8" isCenter={true} />
      <ComponentLabel label="C-term" top={120} left={70} />
      <ComponentBox label="scFab" format={SCFAB} top={160} left={250} textColor="teal.5" />
      <ComponentBox
        label="IgG-[scFab*]"
        format={IGG_SCFAB}
        backgroundColor="teal.0"
        formatSize={150}
        top={50}
        left={515}
      />
    </Example>
  );
}

function Example3(): JSX.Element {
  const veritasName = (
    <VeritasName>
      <SyntaxPart>
        <Text span={true} color="purple.5">
          scFv
        </Text>
      </SyntaxPart>
      <Text span={true} color="gray.5">
        -
      </Text>
      <SyntaxPart>
        <Text span={true} td="underline" color="green.8">
          Fc
        </Text>
      </SyntaxPart>
      <Text span={true} color="gray.5">
        -
      </Text>
      <SyntaxPart>
        <Text span={true} color="teal.5">
          scFv
        </Text>
      </SyntaxPart>
    </VeritasName>
  );
  return (
    <Example veritasName={veritasName} height={430}>
      <Arrow top={75} left={20} width={300} height={110} path="M 20 110 l 0 -100 l 200 0" />
      <Arrow top={120} left={50} width={300} height={100} path="M 30 68 l 0 -30 l 150 0 l 0 50 l 65 0" />
      <Arrow top={220} left={90} width={200} height={200} path="M 30 20 l 0 100 l 120 0" />
      <Arrow top={165} left={410} width={200} height={100} path="M 0 50 l 95 0" />
      <ComponentLabel label="N-term" top={185} left={20} />
      <ComponentLabel label="center" top={185} left={65} />
      <ComponentLabel label="C-term" top={185} left={105} />
      <ComponentBox label="scFv" format={SCFV} top={30} left={250} textColor="purple.5" />
      <ComponentBox label="scFv" format={SCFV} top={30} left={350} textColor="purple.5" />
      <ComponentBox label="Fc" format={FC} top={160} left={300} textColor="green.8" isCenter={true} />
      <ComponentBox label="scFv" format={SCFV} top={290} left={250} textColor="teal.5" />
      <ComponentBox label="scFv" format={SCFV} top={290} left={350} textColor="teal.5" />
      <ComponentBox
        label="scFv-Fc-scFv"
        format={SCFV_FC_SCFV}
        backgroundColor="teal.0"
        formatSize={150}
        top={120}
        left={515}
      />
    </Example>
  );
}

export function ExamplesTab(): JSX.Element {
  return (
    <>
      <svg width="0" height="0" style={{position: 'absolute'}} aria-hidden={true}>
        <defs>
          <marker
            id="veritas-example-arrowhead"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill={Color.GRAY_400} />
          </marker>
        </defs>
      </svg>
      <Example1 />
      <Example2 />
      <Example3 />
    </>
  );
}
